import React, { createContext, useContext, useReducer, useEffect } from 'react';
import storage from '../utils/storage';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { getFirestore, doc, setDoc } from 'firebase/firestore';
import referralService from '../services/referralService';

// Import Firebase configuration
import { firebaseConfig } from '../../firebase.config';

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Set persistence to local (persists across browser sessions)
// This ensures users stay logged in even after closing the browser
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error('Error setting auth persistence:', error);
});

// Action Types
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_USER: 'SET_USER',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Initial State
const initialState = {
  user: null,
  loading: true,
  error: null,
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case ActionTypes.SET_USER:
      return { ...state, user: action.payload, loading: false, error: null };
    
    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case ActionTypes.CLEAR_ERROR:
      return { ...state, error: null };
    
    default:
      return state;
  }
};

// Create Context
const AuthContext = createContext();

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Helper function to convert Firebase errors to user-friendly messages
  const getErrorMessage = (error) => {
    switch (error.code) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return 'Invalid email or password. Please check your credentials and try again.';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists. Please try logging in instead.';
      case 'auth/weak-password':
        return 'Password is too weak. Please choose a stronger password.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection.';
      case 'auth/operation-not-allowed':
        return 'This sign-in method is not enabled. Please contact support.';
      default:
        return 'An error occurred. Please try again.';
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is signed in
        const userData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          emailVerified: user.emailVerified,
        };
        dispatch({ type: ActionTypes.SET_USER, payload: userData });
      } else {
        // User is signed out
        dispatch({ type: ActionTypes.SET_USER, payload: null });
      }
    });

    return unsubscribe;
  }, []);

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });
      dispatch({ type: ActionTypes.CLEAR_ERROR });
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      const userFriendlyMessage = getErrorMessage(error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: userFriendlyMessage });
      throw error;
    }
  };

  // Register with email and password
  const register = async (email, password, displayName, referrerId = null) => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });
      dispatch({ type: ActionTypes.CLEAR_ERROR });
      
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update the user's display name
      if (displayName) {
        await updateProfile(userCredential.user, {
          displayName: displayName
        });
      }

      // Create user document in Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        name: displayName,
        createdAt: new Date(),
        totalStudyTime: 0,
        streak: 0,
        lifelongCardsCreated: 0,
        lifelongSetsCreated: 0,
        lifelongClassesCreated: 0,
        referrals_count: 0,
        referred_by: referrerId || null,
        referral_bonus_claimed: false,
        ai_flashcards_earned: 150, // Free users get 150 AI flashcards on signup
        ai_flashcards_used: 0,
        isOnboarded: true, // Skip onboarding - users can start using the app immediately
      });

      // Process referral if referrerId exists
      if (referrerId) {
        try {
          await referralService.processReferral(referrerId, userCredential.user.uid);
        } catch (referralError) {
          // Don't fail registration if referral processing fails
        }
      }
      
      return userCredential.user;
    } catch (error) {
      const userFriendlyMessage = getErrorMessage(error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: userFriendlyMessage });
      throw error;
    }
  };

  // Sign out
  const signOutUser = async () => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });
      await signOut(auth);
    } catch (error) {
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: ActionTypes.CLEAR_ERROR });
  };

  const value = {
    ...state,
    signIn,
    register,
    signOut: signOutUser,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;